import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import styled, { css } from 'styled-components';
import api from '../services/api';
import { getTenantId } from '../utils/tenantHelper';

const pad2 = (n) => String(Math.max(0, n)).padStart(2, '0');

function splitRemaining(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  return { days, hours, minutes, seconds, totalSec };
}

function useCountdownTarget(targetAt) {
  const targetMs = useMemo(() => {
    if (!targetAt) return null;
    const t = new Date(targetAt).getTime();
    return Number.isNaN(t) ? null : t;
  }, [targetAt]);

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (targetMs == null) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetMs]);

  if (targetMs == null) return null;
  return splitRemaining(targetMs - now);
}

async function fetchCountdown(tenantId) {
  const params = {};
  if (tenantId) params.tenant_id = tenantId;
  const res = await api.get('/public/countdown', { params });
  // api interceptor returns response.body
  return res?.data || res || { enabled: false };
}

const Banner = styled.div`
  ${(p) =>
    p.$variant === 'pinned'
      ? css`
          width: 100%;
          background: linear-gradient(90deg, #043663 0%, #0a4a7a 55%, #043663 100%);
          color: #fff;
          border-bottom: 2px solid #d52b1e;
        `
      : css`
          width: 100%;
          margin: 0 0 0.85rem;
          background: linear-gradient(135deg, #043663 0%, #0a4a7a 100%);
          color: #fff;
          border-radius: 12px;
          border: 1px solid rgba(213, 43, 30, 0.55);
          box-shadow: 0 1px 2px rgba(4, 54, 99, 0.12);
        `}
`;

const Inner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem 1.25rem;
  flex-wrap: wrap;
  padding: ${(p) => (p.$variant === 'pinned' ? '0.55rem 1rem' : '0.75rem 1rem')};
  max-width: ${(p) => (p.$variant === 'pinned' ? '1200px' : 'none')};
  margin: 0 auto;
`;

const Title = styled.p`
  margin: 0;
  font-size: ${(p) => (p.$variant === 'pinned' ? '0.875rem' : '0.9375rem')};
  font-weight: 600;
  letter-spacing: 0.01em;
  text-align: center;
`;

const Units = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const Unit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 2.6rem;
  padding: 0.2rem 0.35rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
`;

const UnitValue = styled.span`
  font-size: 1.05rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
`;

const UnitLabel = styled.span`
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.85;
`;

/**
 * @param {'pinned'|'inline'} variant
 * @param {string} [tenantId] optional override; else X-Tenant-ID / switzerland
 */
export default function RegistrationCountdown({ variant = 'inline', tenantId: tenantIdProp }) {
  const tenantId = tenantIdProp || getTenantId() || undefined;
  const { data } = useQuery(
    ['publicRegistrationCountdown', tenantId || 'default'],
    () => fetchCountdown(tenantId),
    { staleTime: 60_000, refetchOnWindowFocus: true }
  );

  const remaining = useCountdownTarget(data?.enabled ? data.target_at : null);

  if (!data?.enabled || !remaining || remaining.totalSec <= 0) {
    return null;
  }

  return (
    <Banner $variant={variant} role="status" aria-live="polite">
      <Inner $variant={variant}>
        <Title $variant={variant}>{data.title}</Title>
        <Units>
          <Unit>
            <UnitValue>{remaining.days}</UnitValue>
            <UnitLabel>Days</UnitLabel>
          </Unit>
          <Unit>
            <UnitValue>{pad2(remaining.hours)}</UnitValue>
            <UnitLabel>Hrs</UnitLabel>
          </Unit>
          <Unit>
            <UnitValue>{pad2(remaining.minutes)}</UnitValue>
            <UnitLabel>Min</UnitLabel>
          </Unit>
          <Unit>
            <UnitValue>{pad2(remaining.seconds)}</UnitValue>
            <UnitLabel>Sec</UnitLabel>
          </Unit>
        </Units>
      </Inner>
    </Banner>
  );
}
